import { motion } from 'framer-motion';
import { Award, Target, Users, Zap } from 'lucide-react';
import SEO from '@/components/SEO';
const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Propósito',
      description: 'Existimos para transformar a forma como espaços são percebidos no digital. Cada tour que criamos é uma ponte entre interesse e decisão.',
    },
    {
      icon: Zap,
      title: 'Inovação',
      description: 'Tecnologia 360° de ponta combinada com visão comercial. Não fazemos apenas fotos bonitas — criamos ferramentas de venda.',
    },
    {
      icon: Award,
      title: 'Excelência',
      description: 'Do primeiro clique à entrega final, cada pixel é pensado. Nosso padrão é o que faz o cliente voltar e indicar.',
    },
    {
      icon: Users,
      title: 'Parceria',
      description: 'Entendemos seu negócio antes de ligar a câmera. Cada projeto é uma parceria estratégica, não apenas um serviço.',
    },
  ];

  const aboutSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "url": "https://grantour360.com.br/sobre",
        "name": "Sobre a Gran Tour 360",
        "description": "Conheça a Gran Tour 360: especialistas em tours virtuais 360°"
      },
      {
        "@type": "Organization",
        "name": "Gran Tour 360",
        "url": "https://grantour360.com.br",
        "logo": "https://grantour360.com.br/images/logo.png",
        "sameAs": [
          "https://instagram.com/grantour360"
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white pt-32 pb-24" data-testid="about-page">
      <SEO 
        title="Sobre a Gran Tour 360 | Especialistas em Tours Virtuais 360°"
        description="Conheça a Gran Tour 360: especialistas em tours virtuais 360° com visão comercial. Criamos experiências que vendem para imobiliárias, hotéis, restaurantes e concessionárias."
        url="https://grantour360.com.br/sobre"
        schema={aboutSchema}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Sobre Nós</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Não Apenas Fazemos Fotos Bonitas.<br />
              <span className="text-gradient">Criamos Experiências que Vendem.</span>
            </h1>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/7045941/pexels-photo-7045941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Equipe Gran Tour 360 - Especialistas em Tours Virtuais 360°"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
              Onde Cada Detalhe Convida a Entrar
            </h2>
            <div className="space-y-4 text-gray-500 text-base sm:text-lg leading-relaxed">
              <p>
                A <strong className="text-gray-900">Gran Tour 360</strong> nasceu de uma constatação simples: em mercados de alto valor, quem mostra melhor, vende mais.
              </p>
              <p>
                Não nos limitamos a capturar espaços — nós os apresentamos com intencionalidade comercial. Cada ângulo, cada hotspot e cada transição são pensados para guiar o olhar do cliente e acelerar sua tomada de decisão.
              </p>
              <p>
                Atendemos incorporadoras, imobiliárias, hotéis, restaurantes, salões de eventos, empresas e concessionárias de veículos que entendem que a experiência digital precisa ser tão impactante quanto a presencial.
              </p>
              <p>
                Utilizamos tecnologia <strong className="text-gray-900">360°</strong> para criar tours em HTML5, hospedados em subdomínios exclusivos, com compatibilidade total em qualquer dispositivo. Tecnologia de ponta a serviço do seu resultado comercial.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="mb-24">
          <div className="text-center mb-12">
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Nossos Valores</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              O Que Define a Gran Tour 360
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 p-8 rounded-lg text-center hover:border-[#FF3366]/50 hover:shadow-lg transition-all"
                  data-testid={`value-${index}`}
                >
                  <div className="w-16 h-16 bg-[#FF3366]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon size={28} className="text-[#FF3366]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#111111] rounded-xl p-12 text-center"
          data-testid="about-stats"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 tracking-tight">
            Números que Comprovam
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="text-5xl font-bold text-[#FF3366] mb-2">80%</div>
              <p className="text-white/60">Mais engajamento com o espaço</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#FF3366] mb-2">50%</div>
              <p className="text-white/60">Menos visitas improdutivas</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#FF3366] mb-2">24/7</div>
              <p className="text-white/60">Seu espaço sempre aberto</p>
            </div>
          </div>
        </motion.div>

        {/* E-E-A-T Trust Signals Section */}
        <div className="mt-24 pt-16 border-t border-gray-100">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
              Excelência, Transparência e Qualidade
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <h3 className="font-bold text-lg mb-2 text-gray-900">Tecnologia 360° de Ponta</h3>
              <p className="text-gray-500 text-sm leading-relaxed">Nossos ambientes são escaneados em ultra-resolução utilizando frameworks HTML5 puros para máxima performance em qualquer dispositivo, assegurando ausência de travamentos e imersão absoluta.</p>
            </div>
            <div className="p-6">
              <h3 className="font-bold text-lg mb-2 text-gray-900">Privacidade de Dados Assegurada</h3>
              <p className="text-gray-500 text-sm leading-relaxed">Operamos sob estrito sigilo comercial e privacidade local (LGPD), sem coletar ou distribuir dados de visitantes sem consentimento.</p>
            </div>
            <div className="p-6">
              <h3 className="font-bold text-lg mb-2 text-gray-900">Suporte Premium Ativo</h3>
              <p className="text-gray-500 text-sm leading-relaxed">Profissionais baseados em Curitiba, sempre a um chamado no WhatsApp de distância para resolver integrações, trocar hotspots promocionais e refinar seu projeto.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
